import React, { useCallback, useState } from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "../platformComponents";
import { colors, fontFamily, fontSize, margin, padding } from "../styles/common";
import { IS_WEB, padZeroIfLessThanTen } from "@adgm/libs";
import { useGenericFormContext } from "./GenericForm";
import { CommonImage, ModalConfirm } from "../common";

type TProps = {
  /** 序号，用于显示编号（如：01、02等） */
  sequenceNumber?: number;
  label?: string | React.ReactNode;
  /** 是否使用水平布局模式，影响样式和字体大小 */
  horizontalLayout?: boolean;
  required?: boolean;
  tip?: string;
};
/**
 * KYC标签元素组件
 *
 * @param props
 * @param props.sequenceNumber
 * @param props.label
 * @param props.horizontalLayout - 是否使用水平布局模式

 */
export const KycLabelElement: React.FC<TProps> = React.memo(props => {
  const [showTipsModal, setShowTipsModal] = useState(false);
  const { sequenceNumber, label, horizontalLayout, required, tip } = props;
  const { genericFormType } = useGenericFormContext();
  const noSequenceNumber = genericFormType === "information";
  /**
   * 根据布局模式生成自定义文本样式
   * 根据 horizontalLayout 参数和平台（Web/App）返回不同的样式配置：
   */
  const customTextStyle = useCallback(() => {
    // 基础样式：根据布局模式设置文字颜色
    const baseStyle = horizontalLayout ? { color: colors.white14 } : { color: colors.white };

    return IS_WEB
      ? {
          ...baseStyle,
          fontSize: horizontalLayout ? fontSize.f14 : fontSize.f20,
          fontFamily: horizontalLayout ? "Common-Regular" : "Common-Medium",
        }
      : {
          ...baseStyle,
          fontSize: horizontalLayout ? fontSize.f12 : fontSize.f14,
          fontFamily: fontFamily.commonRegular,
        };
  }, [horizontalLayout]);

  const _renderSequenceNumberView = useCallback(() => {
    if (noSequenceNumber || horizontalLayout || sequenceNumber === undefined) return null;

    return (
      <Text style={[styles.label, { color: colors.highlightColor }]}>
        {`${padZeroIfLessThanTen(sequenceNumber)}.`}{" "}
      </Text>
    );
  }, [noSequenceNumber, horizontalLayout, sequenceNumber]);
  return label ? (
    <>
      <View style={{ marginBottom: margin.m8, flexDirection: "row" }}>
        {_renderSequenceNumberView()}
        <View style={{ flex: 1, flexDirection: "row", alignItems: "flex-start" }}>
          {required ? <Text style={{ color: colors.red14, marginRight: margin.m8 }}>*</Text> : null}
          <Text
            style={[
              {
                flex: 1,
                color: colors.white,
              },
              customTextStyle(),
            ]}>
            {label ?? ""}
            {tip ? (
              <TouchableOpacity
                // @ts-ignore platformComponents
                onPress={() => {
                  setShowTipsModal(true);
                }}
                style={{
                  alignSelf: "stretch",
                  paddingHorizontal: padding.p6,
                }}>
                <View style={{ paddingTop: 2.5 }}>
                  <CommonImage
                    source={require("../assets/images/toast.webp")}
                    width={14}
                    height={14}
                  />
                </View>
              </TouchableOpacity>
            ) : null}
          </Text>
        </View>
      </View>
      {showTipsModal ? (
        <ModalConfirm
          showClose={false}
          isVisible={showTipsModal}
          onConfirmClick={() => {
            setShowTipsModal(false);
          }}
          onCancelClick={() => {
            setShowTipsModal(false);
          }}>
          <Text
            style={{
              fontFamily: IS_WEB ? "Common-Regular" : fontFamily.commonRegular,
              color: colors.white,
              fontSize: fontSize.f16,
              paddingVertical: padding.p14,
            }}>
            {tip}
          </Text>
        </ModalConfirm>
      ) : null}
    </>
  ) : null;
});

const styles = StyleSheet.create({
  label: {
    fontFamily: IS_WEB ? "Common-Medium" : fontFamily.commonRegular,
    fontSize: IS_WEB ? fontSize.f20 : fontSize.f16,
  },
});
